import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Alert, 
  Button, 
  IconButton, 
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Menu,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { formatDate, formatDateTime, initializeFormats, getFormatConfig } from '../../../utils/global';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { AuditLoggers } from '../../../utils/auditLogger';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InputAdornment from '@mui/material/InputAdornment';
// Import the common toast components
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';

// Import password validation utility
import { validatePassword, getPasswordPolicyText, getPasswordStrength } from '../../../utils/passwordValidation';
import GlobalUserDrawer from './GlobalUserDrawer';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateUploadOutlinedSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateTextFieldSx,
  harmonatePageSearchFieldSx,
  harmonatePageSearchIconSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateGridActionIconSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import { isHarmonateUser as isServerHarmonateUser } from '../../../auth/serverIdentity';

const GlobalUser = () => {
  const { canAdd, canEdit } = useFunctionPermissions(FC.GLOBAL_USERS);
  const apiRef = useGridApiRef();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    tenant_id: '',
    status: 'active',
    harmonate_user: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [updatingCell, setUpdatingCell] = useState(false);
  const [lastEditedCell, setLastEditedCell] = useState(null);
  // Row actions menu state
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState(null);
  const [rowMenuUserId, setRowMenuUserId] = useState(null);
  // Change password dialog state
  const [pwdDialogOpen, setPwdDialogOpen] = useState(false);
  const [pwdSubmitting, setPwdSubmitting] = useState(false);
  const [pwdForm, setPwdForm] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ strength: 0, feedback: [] });
  
  // Use the common toast hook
  const { toastState, showSuccess, showError, showInfo, hideToast } = useToast();

  // Check if user is Harmonate user
  const isHarmonateUser = isServerHarmonateUser();

  // Filter users based on search query
  const filterUsers = useCallback((usersList, query) => {
    if (!query) return usersList;
    
    const lowercasedQuery = query.toLowerCase();
    return usersList.filter(user => {
      // Convert status to string for comparison if it exists
      const status = user.status !== undefined ? String(user.status) : '';
      return (
        (user.email && user.email.toLowerCase().includes(lowercasedQuery)) ||
        (user.first_name && user.first_name.toLowerCase().includes(lowercasedQuery)) ||
        (user.last_name && user.last_name.toLowerCase().includes(lowercasedQuery)) ||
        (user.tenant_name && user.tenant_name.toLowerCase().includes(lowercasedQuery)) ||
        (status && status.toLowerCase().includes(lowercasedQuery))
      );
    });
  }, []);

  // Handle search input change
  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    setFilteredUsers(filterUsers(users, query));
  };

  // Clear search query
  const handleClearSearch = () => {
    setSearchQuery('');
    setFilteredUsers(users);
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      if (!token) throw new Error('No authentication token found');

      // Fetch all users using the correct endpoint
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) throw new Error(`Failed to fetch users: ${resp.status} ${resp.statusText}`);
      const data = await resp.json();
      const usersData = Array.isArray(data) ? data : [];
      setUsers(usersData);
      setFilteredUsers(filterUsers(usersData, searchQuery));
    } catch (err) {
      setError(err.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/tenants`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) throw new Error('Failed to fetch tenants');
      const data = await resp.json();
      setTenants(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching tenants:', err);
      setTenants([]);
    }
  };

  const handleOpenDialog = () => {
    if (!canAdd) return;
    setOpenDialog(true);
    if (tenants.length === 0) {
      fetchTenants();
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setForm({ email: '', first_name: '', last_name: '', tenant_id: '', status: 'active', harmonate_user: false });
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e) => {
    setForm({ ...form, harmonate_user: e.target.checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canAdd) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) throw new Error('No authentication token found');

      const payload = { 
        ...form, 
        status: form.status === 'active' ? 1 : 0
      };
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const errorMessage = errorData.message || 'Failed to create user';
        
        // Log failed user creation
        await AuditLoggers.globalUserCreated(form.email, tenants.find(t => t.id === form.tenant_id)?.name, false, errorMessage);
        
        throw new Error(errorMessage);
      }

      // Log successful user creation
      const tenantName = tenants.find(t => t.id === form.tenant_id)?.name;
      await AuditLoggers.globalUserCreated(form.email, tenantName);

      handleCloseDialog();
      fetchUsers();
      showSuccess('User created successfully!', 'Success');
    } catch (err) {
      showError(err.message || 'An unexpected error occurred. Please try again.', 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRowClick = (params) => {
    // Only open drawer if not in edit mode
    if (!editMode) {
      setSelectedUserId(params.row.id);
      setDrawerOpen(true);
    }
  };
  
  // Toggle edit mode
  const toggleEditMode = () => {
    if (!editMode && !canEdit) return;
    setEditMode(!editMode);
  };
  
  // Save the edited cell data
  const saveEditedCell = useCallback(async (id, field, value) => {
    if (updatingCell) return;
    
    setUpdatingCell(true);
    try {
      const row = users.find(user => user.id === id);
      if (!row) throw new Error('User not found');
      
      const token = localStorage.getItem('token');
      
      if (!token) throw new Error('No authentication token found');
      
      // Only send the edited field in the request body
      const payload = {
        [field]: field === 'status' ? (value === 'active' ? 1 : 0) : value
      };
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/users/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      if (!resp.ok) throw new Error(`Failed to update user: ${resp.status} ${resp.statusText}`);
      
      // Update was successful
      console.log(`Successfully updated ${field} for user ${id}`);
    } catch (err) {
      console.error('Error updating user:', err);
      showError(err.message, 'Update Failed');
      
      // Revert the change in the local state
      fetchUsers();
    } finally {
      setUpdatingCell(false);
    }
  }, [updatingCell, users, showError]);
  
  // Process row update when cell editing is done
  const processRowUpdate = async (newRow, oldRow) => {
    if (!canEdit) return oldRow;
    // Find which field was changed
    const changedField = Object.keys(newRow).find(field => newRow[field] !== oldRow[field]);
    
    if (changedField && editMode) {
      const token = localStorage.getItem('token');
      
      if (token) {
        console.log(`Saving ${changedField} for user ${newRow.id} immediately`);
        
        try {
          // Special handling for harmonate_user field - use PATCH API
          if (changedField === 'harmonate_user') {
            const harmonateResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/users/${newRow.id}/harmonate-user`, {
              method: 'PATCH',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                harmonateUser: newRow.harmonate_user
              }),
            });
            
            if (!harmonateResp.ok) {
              const errorData = await harmonateResp.json().catch(() => ({}));
              throw new Error(errorData.message || 'Failed to update Harmonate status');
            }
            
            // Log successful Harmonate status update
            const user = users.find(u => u.id === newRow.id);
            const tenantName = user?.tenant_name;
            await AuditLoggers.globalUserHarmonateStatusUpdated(newRow, newRow.harmonate_user, tenantName);
            
            // Update local state
            setUsers(prevUsers => 
              prevUsers.map(row => 
                row.id === newRow.id ? { ...row, [changedField]: newRow[changedField] } : row
              )
            );
            
            showSuccess('User updated successfully', 'Success');
          } else {
            // Regular field update using PUT API
            // Prepare payload with username sync when email changes
            let payload = {
              [changedField]: changedField === 'status' ? (newRow[changedField] === 'active' ? 1 : 0) : newRow[changedField]
            };
            
            // If email was changed, also update username with same value
            if (changedField === 'email') {
              payload.username = newRow.email;
            }
            
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/users/${newRow.id}`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(payload),
            });
            
            if (!resp.ok) {
              const errorData = await resp.json().catch(() => ({}));
              throw new Error(errorData.message || `Failed to update ${changedField}`);
            }
            
            // Log successful user update
            const user = users.find(u => u.id === newRow.id);
            const tenantName = user?.tenant_name;
            await AuditLoggers.globalUserUpdated(newRow, changedField, newRow[changedField], tenantName);
            
            // Update local state
            setUsers(prevUsers => 
              prevUsers.map(row => 
                row.id === newRow.id ? { 
                  ...row, 
                  [changedField]: changedField === 'status' ? 
                    (newRow[changedField] === 'active' ? 1 : 0) : 
                    newRow[changedField] 
                } : row
              )
            );
            
            showSuccess('User updated successfully', 'Success');
          }
        } catch (err) {
          console.error(`Error updating ${changedField}:`, err);
          showError(err.message || `Failed to update ${changedField}`, 'Update Failed');
          
          // Revert the change in the local state by refetching users
          fetchUsers();
        }
      }
    }
    
    // Always return the old row to prevent immediate UI update
    return oldRow;
  };

  // Row actions menu handlers
  const openRowMenu = (event, userId) => {
    event.stopPropagation();
    setRowMenuAnchorEl(event.currentTarget);
    setRowMenuUserId(userId);
  };

  const closeRowMenu = () => {
    setRowMenuAnchorEl(null);
  };



  // Change password dialog handlers
  const handleOpenChangePassword = () => {
    closeRowMenu();
    // Get the selected user's email
    const selectedUser = users.find(user => user.id === rowMenuUserId);
    setSelectedUserEmail(selectedUser?.email || '');
    setPwdForm({ new_password: '', confirm_password: '' });
    setPwdDialogOpen(true);
  };

  const handleCloseChangePassword = () => {
    setPwdDialogOpen(false);
    setPwdForm({ new_password: '', confirm_password: '' });
    setSelectedUserEmail('');
    setPasswordStrength({ strength: 0, feedback: [] });
  };

  const handlePwdFormChange = (e) => {
    const { name, value } = e.target;
    setPwdForm({ ...pwdForm, [name]: value });
    
    // Update password strength when new password changes
    if (name === 'new_password') {
      const strength = getPasswordStrength(value);
      setPasswordStrength(strength);
    }
  };

  const handleSubmitChangePassword = async (e) => {
    e.preventDefault();
    if (!rowMenuUserId) return;
    if (!pwdForm.new_password || !pwdForm.confirm_password) {
      showError('Please fill in all password fields.', 'Validation');
      return;
    }
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      showError('New password and confirm password do not match.', 'Validation');
      return;
    }

    // Validate password policy
    const passwordValidation = validatePassword(pwdForm.new_password);
    if (!passwordValidation.isValid) {
      showError(`Password validation failed: ${passwordValidation.errors.join(', ')}`, 'Password Policy');
      return;
    }

    setPwdSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/users/${rowMenuUserId}/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          new_password: pwdForm.new_password,
        }),
      });

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        const errorMessage = data.message || 'Failed to change password';
        const user = users.find(u => u.id === rowMenuUserId);
        
        // Log failed password change
        await AuditLoggers.globalUserPasswordChanged(rowMenuUserId, user?.tenant_name, false, errorMessage);
        
        throw new Error(errorMessage);
      }
      
      // Log successful password change
      const user = users.find(u => u.id === rowMenuUserId);
      const tenantName = user?.tenant_name;
      await AuditLoggers.globalUserPasswordChanged(rowMenuUserId, tenantName);

      handleCloseChangePassword();
      showSuccess('Password updated successfully', 'Success');
    } catch (err) {
      showError(err.message || 'Failed to change password', 'Error');
    } finally {
      setPwdSubmitting(false);
    }
  };

  useEffect(() => {
    if (isHarmonateUser) {
      fetchUsers();
    }
  }, [isHarmonateUser]);

  // If not Harmonate user, show access denied
  if (!isHarmonateUser) {
    return (
      <Box sx={{ ...harmonatePageSx, height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <Alert severity="error" sx={{ maxWidth: '500px' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Access Denied
          </Typography>
          <Typography variant="body2">
            Global User Management is only available for Harmonate administrators.
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ ...harmonatePageSx, height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: ht.white }} />
        <Typography variant="body2" sx={{ ml: 2, color: ht.white }}>Loading global users...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...harmonatePageSx, height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <Alert severity="error" sx={{ mb: 3, maxWidth: '500px' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Failed to load global users
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        <Button 
          variant="contained" 
          startIcon={<RefreshIcon />}
          onClick={fetchUsers} 
          sx={harmonateProceedButtonSx}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const columns = [
    { 
      field: 'email', 
      headerName: 'Email', 
      flex: 1.5,
      editable: editMode
    },
    { 
      field: 'first_name', 
      headerName: 'First Name', 
      flex: 1,
      editable: editMode
    },
    { 
      field: 'last_name', 
      headerName: 'Last Name', 
      flex: 1,
      editable: editMode
    },
    { 
      field: 'created_at', 
      headerName: 'Created At', 
      flex: 1,
      type: 'dateTime',
      valueFormatter: (params) => {
        if (!params.value) return 'N/A';
        // Ensure the date string is in a format that can be parsed
        const dateValue = new Date(params.value);
        if (isNaN(dateValue.getTime())) {
          // If date parsing fails, try to handle common date string formats
          const dateStr = params.value.replace(' ', 'T') + 'Z';
          const altDate = new Date(dateStr);
          return isNaN(altDate.getTime()) ? 'Invalid Date' : formatDateTime(altDate);
        }
        return formatDateTime(dateValue);
      }
    },
    { 
      field: 'updated_at', 
      headerName: 'Last Updated', 
      flex: 1,
      type: 'dateTime',
      valueFormatter: (params) => {
        if (!params.value) return 'N/A';
        return formatDateTime(params.value);
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      editable: editMode,
      type: 'singleSelect',
      valueOptions: ['active', 'inactive'],
      renderCell: (params) => {
        const rawStatus = params.value;
        
        // Handle null, undefined, or non-string values
        let status = 'inactive';
        if (rawStatus && typeof rawStatus === 'string') {
          status = rawStatus;
        }
        
        const isActive = status.toLowerCase() === 'active';
        
        return (
          <Chip
            label={status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
            size="small"
            sx={{
              bgcolor: isActive ? 'rgba(2, 107, 29, 0.12)' : 'rgba(239, 63, 63, 0.12)',
              color: isActive ? ht.green : ht.red,
              borderRadius: ht.buttonRadius,
              fontWeight: 500,
              fontSize: '0.75rem',
              height: 24,
              '& .MuiChip-label': {
                px: 1.5
              }
            }}
          />
        );
      },
      renderEditCell: (params) => {
        const { id, value, field } = params;
        
        return (
          <FormControl fullWidth size="small">
            <Select
              value={value || 'inactive'}
              onChange={(event) => {
                params.api.setEditCellValue({ id, field, value: event.target.value });
              }}
              MenuProps={harmonateSelectMenuProps}
              sx={{
                borderRadius: ht.fieldRadius,
                fontSize: '0.875rem',
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                  fontSize: '0.875rem'
                },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: ht.text },
              }}
            >
              <MenuItem value="active" sx={{ fontSize: '0.875rem' }}>Active</MenuItem>
              <MenuItem value="inactive" sx={{ fontSize: '0.875rem' }}>Inactive</MenuItem>
            </Select>
          </FormControl>
        );
      }
    },
    {
      field: 'harmonate_user',
      headerName: 'Harmonate User',
      width: 140,
      editable: editMode,
      type: 'boolean',
      renderCell: (params) => {
        const value = params.value;
        const isHarmonate = value === true || value === 'true' || value === 1;
        
        return (
          <Chip
            label={isHarmonate ? 'Yes' : 'No'}
            size="small"
            sx={{
              bgcolor: isHarmonate ? ht.secondaryTab : ht.listBg,
              color: isHarmonate ? ht.text : ht.text,
              borderRadius: ht.buttonRadius,
              fontWeight: 500,
              fontSize: '0.75rem',
              height: 24,
              '& .MuiChip-label': {
                px: 1.5
              }
            }}
          />
        );
      },
      renderEditCell: (params) => {
        const { id, value, field } = params;
        const isChecked = value === true || value === 'true' || value === 1;
        
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                onChange={(event) => {
                  params.api.setEditCellValue({ id, field, value: event.target.checked });
                }}
                size="small"
                sx={{
                  color: ht.line,
                  '&.Mui-checked': { color: ht.green },
                }}
              />
            }
            label=""
            sx={{ margin: 0 }}
          />
        );
      }
    },
    {
      field: '_actions',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => openRowMenu(e, params.row.id)} sx={harmonateGridActionIconSx}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        <Box>
          <Typography variant="h6" sx={harmonatePageTitleSx}>
            Global User Management
          </Typography>
          <Typography variant="body2" sx={{ color: ht.white, mt: 0.5, fontSize: '0.875rem' }}>
            Manage all users across all tenants (Harmonate Admin Only)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search users..."
            value={searchQuery}
            onChange={handleSearchChange}
            variant="outlined"
            sx={{ ...harmonatePageSearchFieldSx, width: 300, mr: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={harmonatePageSearchIconSx} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    edge="end"
                    sx={{ color: ht.white }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={canEdit ? (editMode ? 'Exit Edit Mode' : 'Enable Cell Editing') : 'No edit permission'}>
            <span>
              <IconButton 
                onClick={toggleEditMode}
                disabled={!canEdit}
                sx={{ 
                  ...harmonatePlainIconButtonSx,
                  color: editMode ? ht.secondaryTab : ht.white,
                }}
              >
                <EditOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canAdd ? 'Add User' : 'No add permission'}>
            <span>
              <Button 
                onClick={handleOpenDialog} 
                variant="contained" 
                startIcon={<PersonAddIcon />}
                disabled={!canAdd}
                sx={harmonateProceedButtonSx}
              >
                Add User
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
      
      <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataGridPro
          rowHeight={40}
          apiRef={apiRef}
          rows={filteredUsers.map((user, idx) => ({
            id: user.id || user.rec_id || idx + 1,
            email: user.email || '',
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            created_at: user.created_at || '',
            status: user.status === 1 ? 'active' : 'inactive',
            harmonate_user: user.harmonate_user,
            _actions: user.id || user.rec_id || idx + 1,
          }))}
          columns={columns}
          columnResizeMode="onEnd"
          resizable
          autoHeight={false}
          sx={{ 
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            height: '100%',
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
            '& .MuiDataGrid-cell--editing': {
              backgroundColor: `${ht.secondaryTab} !important`,
              boxShadow: 'none !important',
            },
          }}
          editMode="cell"
          processRowUpdate={processRowUpdate}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          onRowClick={handleRowClick}
          pagination
          rowsPerPageOptions={[10, 25, 50]}
          pageSize={10}
          autoPageSize={false}
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="body1" sx={{ mb: 2, color: ht.text }}>
                  No global users available
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={fetchUsers}
                  sx={harmonateUploadOutlinedSx}
                >
                  Refresh
                </Button>
              </Box>
            )
          }}
        />
      </Paper>

      {/* Add User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Add Global User</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <TextField 
              label="Email" 
              name="email" 
              value={form.email} 
              onChange={handleFormChange} 
              required 
              fullWidth 
              type="email"
              size="small"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
            <TextField 
              label="First Name" 
              name="first_name" 
              value={form.first_name} 
              onChange={handleFormChange} 
              required 
              fullWidth 
              size="small"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
            <TextField 
              label="Last Name" 
              name="last_name" 
              value={form.last_name} 
              onChange={handleFormChange} 
              required 
              fullWidth 
              size="small"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
            <TextField
              select
              label="Status"
              name="status"
              value={form.status}
              onChange={handleFormChange}
              fullWidth
              size="small"
              InputLabelProps={harmonateInputLabelProps}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              sx={harmonateTextFieldSx}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
            <TextField
              select
              label="Assign to Tenant (Optional)"
              name="tenant_id"
              value={form.tenant_id}
              onChange={handleFormChange}
              fullWidth
              size="small"
              InputLabelProps={harmonateInputLabelProps}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              sx={harmonateTextFieldSx}
            >
              <MenuItem value="">
                <em>No tenant assignment</em>
              </MenuItem>
              {tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.harmonate_user}
                  onChange={handleCheckboxChange}
                  sx={{
                    color: ht.line,
                    '&.Mui-checked': { color: ht.green },
                  }}
                />
              }
              label="Harmonate User"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: ht.text } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseDialog} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={submitting} 
              sx={harmonateProceedButtonSx}
            >
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Row actions menu */}
      <Menu
        anchorEl={rowMenuAnchorEl}
        open={Boolean(rowMenuAnchorEl)}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: ht.fieldRadius,
            '& .MuiMenuItem-root': { fontSize: '0.875rem', color: ht.text },
          },
        }}
      >
        <MenuItem onClick={handleOpenChangePassword}>Change Password</MenuItem>
      </Menu>

      {/* Change Password Dialog */}
      <Dialog open={pwdDialogOpen} onClose={handleCloseChangePassword} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Change Password</DialogTitle>
        <form onSubmit={handleSubmitChangePassword}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            {selectedUserEmail && (
              <Typography variant="body2" sx={{ mb: 1, color: ht.text, fontSize: '0.875rem' }}>
                Changing password for: <strong>{selectedUserEmail}</strong>
              </Typography>
            )}
            <TextField
              label="New Password"
              name="new_password"
              type="password"
              value={pwdForm.new_password}
              onChange={handlePwdFormChange}
              required
              fullWidth
              size="small"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
            {pwdForm.new_password && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: ht.text }}>
                  Password Strength: {Math.round(passwordStrength.strength * 100)}%
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                  {[1, 2, 3].map((level) => (
                    <Box
                      key={level}
                      sx={{
                        height: 4,
                        flex: 1,
                        bgcolor: passwordStrength.strength >= level / 3 ? 
                          (passwordStrength.strength >= 0.7 ? ht.green : 
                           passwordStrength.strength >= 0.4 ? ht.blue : ht.red) : ht.line,
                        borderRadius: 1
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" sx={{ color: ht.red }}>
                  Requirements: {passwordStrength.feedback.join(', ')}
                </Typography>
              </Box>
            )}
            <TextField
              label="Confirm New Password"
              name="confirm_password"
              type="password"
              value={pwdForm.confirm_password}
              onChange={handlePwdFormChange}
              required
              fullWidth
              size="small"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseChangePassword} disabled={pwdSubmitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={pwdSubmitting} sx={harmonateProceedButtonSx}>
              {pwdSubmitting ? 'Updating...' : 'Update'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Global User Drawer */}
      <GlobalUserDrawer 
        open={drawerOpen} 
        onClose={() => setDrawerOpen(false)} 
        userId={selectedUserId} 
      />

      {/* Common Toast Component */}
      <Toast 
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
        variant={toastState.variant}
      />
    </Box>
  );
};

export default GlobalUser;
